// export default GrievanceForm;
import React, { useState } from "react";
import { CheckCircle, Mail, Phone, MessageSquare, AlertCircle, Send, Shield, Clock, Headphones } from "lucide-react";
import axiosInstance from "@/axios";
import { toast } from "react-toastify";

const GrievanceForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contactNumber: "",
    alternateNumber: "",
    category: "",
    subCategory: "",
    concern: "",
    agreeToTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = "Contact number is required";
    } else if (!/^\d{9,10}$/.test(formData.contactNumber)) {
      newErrors.contactNumber = "Contact number must be 9-10 digits";
    }

    if (formData.alternateNumber && !/^\d{9,10}$/.test(formData.alternateNumber)) {
      newErrors.alternateNumber = "Alternate number must be 9-10 digits";
    }

    if (!formData.category) {
      newErrors.category = "Please select a category";
    }

    if (!formData.subCategory) {
      newErrors.subCategory = "Please select a sub-category";
    }

    if (!formData.concern.trim()) {
      newErrors.concern = "Please explain your concern";
    } else if (formData.concern.length > 500) {
      newErrors.concern = "Concern should not exceed 500 words";
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if(!validateForm())
    {
      return;
    }
    try {
      await axiosInstance.post("/request/grievance-redressal", formData);

      setSubmitted(true);
      toast.success("Message sent successfully!");
      setFormData({
        name: "",
        email: "",
        contactNumber: "",
        alternateNumber: "",
        category: "",
        subCategory: "",
        concern: "",
        agreeToTerms: false,
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    }
    // if (validateForm()) {
    //   setIsSubmitting(true);
    //   setTimeout(() => {
    //     setSubmitted(true);
    //     setIsSubmitting(false);
    //   }, 2000);
    // }
  };

  const categories = [
    "Course Content",
    "Technical Issue",
    "Billing/Payment",
    "Instructor",
    "Certificate",
  ];

  const subCategories = {
    "Course Content": ["Quality", "Outdated", "Missing", "Incorrect"],
    "Technical Issue": ["Login", "Video Playback", "Download", "Assessment"],
    "Billing/Payment": ["Refund", "Charge Error", "Payment Failed", "Invoice"],
    "Instructor": ["Responsiveness", "Knowledge", "Teaching Style"],
    "Certificate": ["Not Received", "Error in Certificate", "Verification"],
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center max-w-md w-full transform animate-pulse">
          <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Thank You!</h2>
          <p className="text-gray-600 leading-relaxed">
            Your grievance has been submitted successfully. We will respond to your concerns within 48 business hours.
          </p>
          <div className="mt-8 p-4 bg-green-50 rounded-xl">
            <p className="text-green-700 text-sm font-medium">
              Reference ID: GRV{Math.floor(Math.random() * 10000)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (

    <div className="p-8 lg:p-12 bg-white">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <MessageSquare className="w-6 h-6 text-blue-600 mr-3" />
          <h2 className="text-2xl font-bold text-gray-800">Submit Your Concern</h2>
        </div>
        <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"></div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <div className="group">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Name of the Learner *
          </label>
          <div className="relative">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 focus:outline-none ${errors.name
                  ? "border-red-400 bg-red-50 focus:border-red-500"
                  : "border-gray-200 focus:border-blue-500 focus:bg-blue-50"
                }`}
              placeholder="Enter your full name"
            />
            {errors.name && (
              <div className="!absolute right-3 top-1/2 transform -translate-y-1/2">
                <AlertCircle className="w-5 h-5 text-red-500" />
              </div>
            )}
          </div>
          {errors.name && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.name}
            </p>
          )}
        </div>

        {/* Email Field */}
        <div className="group">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Registered Email ID *
          </label>
          <div className="relative">
            <Mail className="!absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full !pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-300 focus:outline-none ${errors.email
                  ? "border-red-400 bg-red-50 focus:border-red-500"
                  : "border-gray-200 focus:border-blue-500 focus:bg-blue-50"
                }`}
              placeholder="Enter your email address"
            />
          </div>
          {errors.email && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.email}
            </p>
          )}
        </div>

        {/* Contact Number */}
        <div className="group">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Contact Number *
          </label>
          <div className="flex rounded-xl overflow-hidden border-2 border-gray-200 focus-within:border-blue-500 transition-all duration-300">
            <div className="bg-gray-50 px-4 py-3 border-r border-gray-200 flex items-center">
              <span className="text-gray-600 font-medium">+91</span>
            </div>
            <div className="relative flex-1">
              <Phone className="!absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                className={`w-full !pl-12 pr-4 py-3 !border !border-none focus:outline-none ${errors.contactNumber ? "bg-red-50" : "focus:bg-blue-50"
                  }`}
                placeholder="Enter contact number"
              />
            </div>
          </div>
          {errors.contactNumber && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.contactNumber}
            </p>
          )}
        </div>

        {/* Alternate Number */}
        <div className="group">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Alternate Contact Number
          </label>
          <div className="flex rounded-xl overflow-hidden border-2 border-gray-200 focus-within:border-blue-500 transition-all duration-300">
            <div className="bg-gray-50 px-4 py-3 border-r border-gray-200 flex items-center">
              <span className="text-gray-600 font-medium">+91</span>
            </div>
            <input
              type="text"
              name="alternateNumber"
              value={formData.alternateNumber}
              onChange={handleChange}
              className={`w-full px-4 py-3 focus:outline-none !border !border-none ${errors.alternateNumber ? "bg-red-50" : "focus:bg-blue-50"
                }`}
              placeholder="Enter alternate number (optional)"
            />
          </div>
          {errors.alternateNumber && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.alternateNumber}
            </p>
          )}
        </div>

        {/* Category Dropdowns */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="group">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Category of Complaint *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 focus:outline-none ${errors.category
                  ? "border-red-400 bg-red-50 focus:border-red-500"
                  : "border-gray-200 focus:border-blue-500 focus:bg-blue-50"
                }`}
            >
              <option value="">Select Category</option>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            {errors.category && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.category}
              </p>
            )}
          </div>

          <div className="group">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sub Category *
            </label>
            <select
              name="subCategory"
              value={formData.subCategory}
              onChange={handleChange}
              disabled={!formData.category}
              className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 focus:outline-none ${!formData.category
                  ? "bg-gray-100 cursor-not-allowed"
                  : errors.subCategory
                    ? "border-red-400 bg-red-50 focus:border-red-500"
                    : "border-gray-200 focus:border-blue-500 focus:bg-blue-50"
                }`}
            >
              <option value="">Select Sub Category</option>
              {formData.category && subCategories[formData.category]?.map((subCategory) => (
                <option key={subCategory} value={subCategory}>{subCategory}</option>
              ))}
            </select>
            {errors.subCategory && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.subCategory}
              </p>
            )}
          </div>
        </div>

        {/* Concern Textarea */}
        <div className="group">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Please explain your concern *
          </label>
          <textarea
            name="concern"
            value={formData.concern}
            onChange={handleChange}
            rows="5"
            className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 focus:outline-none resize-none ${errors.concern
                ? "border-red-400 bg-red-50 focus:border-red-500"
                : "border-gray-200 focus:border-blue-500 focus:bg-blue-50"
              }`}
            placeholder="Describe your concern in detail (max 500 words)"
          ></textarea>
          <div className="flex justify-between items-center mt-2">
            {errors.concern ? (
              <p className="text-red-500 text-sm flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.concern}
              </p>
            ) : (
              <div></div>
            )}
            <span className={`text-sm ${formData.concern.length > 450 ? 'text-orange-500' : 'text-gray-500'}`}>
              {formData.concern.length}/500
            </span>
          </div>
        </div>

        {/* Terms Checkbox */}
        <div className="group">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              name="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={handleChange}
              className="mt-1 mr-3 w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600 leading-relaxed">
              I confirm that I have completely read, understood and expressly agreed to the terms of yslearn{" "}
              <a href="#" className="text-blue-600 hover:text-blue-800 underline">
                Privacy Policy
              </a>{" "}
              prior to submitting this form.
            </span>
          </label>
          {errors.agreeToTerms && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.agreeToTerms}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-300 transform ${isSubmitting
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:scale-105 shadow-lg hover:shadow-xl"
            }`}
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Submitting...
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <Send className="w-5 h-5 mr-2" />
              Submit Grievance
            </div>
          )}
        </button>
      </form>
    </div>

  );
};
export default GrievanceForm;